"use client";

import { useMemo, useState } from "react";
import { MessageSquare, Minus, Plus, Sparkles } from "lucide-react";

import { ScrollReveal } from "@/app/components/marketing/ScrollReveal";
import { LEGAL_PODPORA_EMAIL } from "@/app/legal/legal-meta";

export type MarketingFaqEntry = {
  id: number;
  q: string;
  a: string;
};

function MarketingFaqAccordionItem({
  faq,
  expanded,
  onToggle,
}: {
  faq: MarketingFaqEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl transition-colors duration-200 ${
        expanded
          ? "border border-indigo-500/25 bg-[#121829]/90"
          : "border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.035]"
      }`}
    >
      <button
        type="button"
        id={`faq-q-${faq.id}`}
        aria-expanded={expanded}
        aria-controls={`faq-p-${faq.id}`}
        onClick={onToggle}
        className="flex w-full min-h-[44px] items-center justify-between gap-3 px-4 py-3.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f29] sm:px-5 sm:py-4"
      >
        <span
          className={`pr-2 font-jakarta text-[14px] font-bold leading-snug sm:text-[15px] ${
            expanded ? "text-indigo-200" : "text-slate-200"
          }`}
        >
          {faq.q}
        </span>
        <div
          aria-hidden
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all duration-300 motion-reduce:transition-none ${
            expanded ? "rotate-180 bg-indigo-500/20 text-indigo-400" : "bg-white/5 text-slate-400 hover:bg-white/10"
          }`}
        >
          {expanded ? <Minus size={16} strokeWidth={2.5} aria-hidden /> : <Plus size={16} strokeWidth={2.5} aria-hidden />}
        </div>
      </button>
      <div
        id={`faq-p-${faq.id}`}
        role="region"
        aria-labelledby={`faq-q-${faq.id}`}
        aria-hidden={!expanded}
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`px-4 text-sm font-normal leading-relaxed text-slate-300 sm:px-5 ${
              expanded ? "pb-4 opacity-100 sm:pb-5" : "pb-0 opacity-0"
            }`}
          >
            {faq.a}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketingFaqSection({
  items,
  sectionId = "faq",
  scrollMtClass = "scroll-mt-28",
  className = "",
  showSupportFooter = true,
}: {
  items: readonly MarketingFaqEntry[];
  sectionId?: string;
  scrollMtClass?: string;
  className?: string;
  showSupportFooter?: boolean;
}) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const splitIndex = useMemo(() => Math.ceil(items.length / 2), [items.length]);

  const left = items.slice(0, splitIndex);
  const right = items.slice(splitIndex);

  return (
    <section
      id={sectionId}
      data-marketing-nav-tone="dark"
      className={`border-t border-white/10 bg-[#0a0f29] px-5 py-20 md:px-6 md:py-24 ${scrollMtClass} ${className}`}
    >
      <div className="relative z-10 mx-auto max-w-[1140px]">
        <ScrollReveal>
          <div className="mb-10 text-center md:mb-12">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-300">
              <Sparkles size={13} className="text-indigo-400" aria-hidden />
              Nápověda k platformě
            </div>
            <h2 className="mb-4 font-jakarta text-3xl font-bold tracking-tight text-white md:text-5xl">Časté otázky</h2>
            <p className="mx-auto max-w-xl text-sm font-medium text-slate-400 md:text-lg">
              Odpovědi před registrací.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2 lg:gap-x-5 lg:gap-y-3">
          <div className="flex flex-col gap-3">
            {left.map((faq) => (
              <MarketingFaqAccordionItem
                key={faq.id}
                faq={faq}
                expanded={openFaq === faq.id}
                onToggle={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
              />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {right.map((faq) => (
              <MarketingFaqAccordionItem
                key={faq.id}
                faq={faq}
                expanded={openFaq === faq.id}
                onToggle={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
              />
            ))}
          </div>
        </div>

        {showSupportFooter ? (
          <div className="mt-12 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:flex-row md:mt-14 md:p-7">
            <div className="text-center sm:text-left">
              <h3 className="mb-1.5 font-jakarta text-base font-bold text-white md:text-lg">Nenašli jste odpověď?</h3>
              <p className="text-sm text-slate-400">
                Napište na podporu — ozveme se v pracovní době.
              </p>
            </div>
            <a
              href={`mailto:${LEGAL_PODPORA_EMAIL}?subject=${encodeURIComponent("Dotaz z webu — Aidvisora")}`}
              className="inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-jakarta font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f29]"
            >
              <MessageSquare size={16} aria-hidden />
              Napište nám
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}
